<template>
  <div class="email-templates">
    <mainmenu selected="email-templates"></mainmenu>

    <div class="admin-page-shell email-template-shell">
      <div class="admin-toolbar">
        <div>
          <h1>{{ $t("email_templates.page_title") }}</h1>
          <div class="toolbar-subtitle">{{ $t("email_templates.subtitle") }}</div>
        </div>
        <div class="toolbar-actions">
          <el-tag type="warning" effect="plain">{{ $t("email_templates.preview_only") }}</el-tag>
          <el-tag v-if="HasUnsavedChanges" type="danger" effect="plain">{{ $t("email_templates.unsaved_changes") }}</el-tag>
          <el-button
            type="primary"
            icon="el-icon-check"
            :loading="saving"
            :disabled="!HasUnsavedChanges"
            @click="save_template">
            {{ $t("email_templates.save") }}
          </el-button>
          <el-button icon="el-icon-refresh" @click="load_templates">{{ $t("email_templates.reload") }}</el-button>
        </div>
      </div>

      <div class="template-workbench" v-loading="loading">
        <section class="template-panel setup-panel">
          <div class="panel-heading">
            <h2>{{ $t("email_templates.configuration") }}</h2>
          </div>

          <div class="setup-static">
            <div class="field-stack">
              <label>{{ $t("email_templates.template") }}</label>
              <el-select v-model="Form.Event" @change="on_template_change" filterable>
                <el-option
                  v-for="template in Templates"
                  :key="template.Id"
                  :label="template.Name"
                  :value="template.Id">
                  <span>{{ template.Name }}</span>
                  <small class="option-help">{{ template.Description }}</small>
                </el-option>
              </el-select>
            </div>

            <div class="field-stack">
              <label>{{ $t("email_templates.language") }}</label>
              <el-select v-model="Form.Language" @change="on_language_change">
                <el-option v-for="language in Languages" :key="language.Id" :label="language.Name" :value="language.Id"></el-option>
              </el-select>
            </div>

            <div class="template-description" v-if="CurrentTemplate">
              {{ CurrentTemplate.Description }}
            </div>
          </div>

          <div class="section-label">
            {{ $t("email_templates.values") }}
          </div>

          <div class="sample-grid">
            <div class="field-stack" v-for="field in Fields" :key="field.Key">
              <label>{{ field.Label }}</label>
              <el-input v-model="Payload[field.Key]" @input="schedule_preview"></el-input>
              <small>{{ field.Placeholder }}</small>
            </div>
          </div>
        </section>

        <section class="template-panel editor-panel" :class="{ fullscreen: EditorFullscreen }">
          <div class="panel-heading">
            <h2>{{ $t("email_templates.editor") }}</h2>
            <el-button size="mini" icon="el-icon-refresh-left" @click="reset_template_text">{{ $t("email_templates.reset_template") }}</el-button>
          </div>

          <div class="field-stack">
            <label>{{ $t("email_templates.subject") }}</label>
            <el-input v-model="Form.Subject" @input="schedule_preview"></el-input>
          </div>

          <div class="field-stack body-editor">
            <label>{{ $t("email_templates.body") }}</label>
            <div class="editor-toolbar">
              <div class="toolbar-group">
                <el-tooltip :content="$t('email_templates.bold')" placement="top">
                  <button type="button" class="format-button bold-button" @click="format_body('bold')">B</button>
                </el-tooltip>
                <el-tooltip :content="$t('email_templates.italic')" placement="top">
                  <button type="button" class="format-button italic-button" @click="format_body('italic')">I</button>
                </el-tooltip>
                <el-tooltip :content="$t('email_templates.underline')" placement="top">
                  <button type="button" class="format-button underline-button" @click="format_body('underline')">U</button>
                </el-tooltip>
                <el-tooltip :content="$t('email_templates.clear_format')" placement="top">
                  <button type="button" class="format-button" @click="format_body('clear')">Tx</button>
                </el-tooltip>
              </div>

              <div class="toolbar-group">
                <el-tooltip :content="$t('email_templates.numbered_list')" placement="top">
                  <button type="button" class="format-button wide-button" @click="format_body('ol')">1.</button>
                </el-tooltip>
                <el-tooltip :content="$t('email_templates.bullet_list')" placement="top">
                  <button type="button" class="format-button" @click="format_body('ul')">•</button>
                </el-tooltip>
                <el-tooltip :content="$t('email_templates.outdent')" placement="top">
                  <button type="button" class="format-button" @click="format_body('outdent')">←</button>
                </el-tooltip>
                <el-tooltip :content="$t('email_templates.indent')" placement="top">
                  <button type="button" class="format-button" @click="format_body('indent')">→</button>
                </el-tooltip>
              </div>

              <div class="toolbar-group">
                <el-tooltip :content="$t('email_templates.link')" placement="top">
                  <button type="button" class="format-button" @click="format_body('link')"><i class="el-icon-link"></i></button>
                </el-tooltip>
                <el-tooltip :content="$t('email_templates.unlink')" placement="top">
                  <button type="button" class="format-button muted-button" @click="format_body('unlink')">×</button>
                </el-tooltip>
                <el-tooltip :content="$t('email_templates.table')" placement="top">
                  <button type="button" class="format-button" @click="format_body('table')">▦</button>
                </el-tooltip>
                <el-tooltip :content="$t('email_templates.horizontal_rule')" placement="top">
                  <button type="button" class="format-button" @click="format_body('hr')">―</button>
                </el-tooltip>
              </div>

              <div class="toolbar-group">
                <el-dropdown trigger="click" @command="format_body">
                  <button type="button" class="format-select">
                    {{ $t("email_templates.format") }} <i class="el-icon-arrow-down"></i>
                  </button>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item command="paragraph">{{ $t("email_templates.paragraph") }}</el-dropdown-item>
                    <el-dropdown-item command="heading">{{ $t("email_templates.heading") }}</el-dropdown-item>
                    <el-dropdown-item command="subheading">{{ $t("email_templates.subheading") }}</el-dropdown-item>
                    <el-dropdown-item command="note">{{ $t("email_templates.note") }}</el-dropdown-item>
                    <el-dropdown-item command="quote">{{ $t("email_templates.quote") }}</el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
                <el-tooltip :content="EditorFullscreen ? $t('email_templates.exit_fullscreen') : $t('email_templates.fullscreen')" placement="top">
                  <button type="button" class="format-button" @click="toggle_fullscreen">
                    <i :class="EditorFullscreen ? 'el-icon-close' : 'el-icon-full-screen'"></i>
                  </button>
                </el-tooltip>
              </div>
            </div>

            <el-input
              ref="body_editor"
              type="textarea"
              :rows="18"
              resize="none"
              v-model="Form.BodyText"
              @input="schedule_preview">
            </el-input>
          </div>

          <div class="placeholder-bank">
            <button
              v-for="field in Fields"
              :key="'placeholder-' + field.Key"
              type="button"
              @click="append_placeholder(field)">
              {{ primary_placeholder(field.Placeholder) }}
            </button>
          </div>
        </section>

        <section class="template-panel preview-panel">
          <div class="panel-heading">
            <h2>{{ $t("email_templates.preview") }}</h2>
            <el-button type="primary" icon="el-icon-view" @click="render_preview">{{ $t("email_templates.update_preview") }}</el-button>
          </div>

          <div class="email-canvas">
            <div class="preview-meta">
              <span>{{ selected_template_label }}</span>
              <strong>{{ Form.Language.toUpperCase() }}</strong>
            </div>

            <div class="email-preview" v-if="Preview.Subject || Preview.BodyHtml">
              <div class="message-meta">
                <div class="message-avatar">M</div>
                <div>
                  <strong>Marketplace Team</strong>
                  <span>{{ Payload.RecipientName }}</span>
                </div>
              </div>
              <div class="email-header">
                <span>{{ $t("email_templates.subject") }}</span>
                <strong>{{ Preview.Subject }}</strong>
              </div>
              <div class="email-body" v-html="Preview.BodyHtml"></div>
            </div>

            <div class="empty-preview" v-else>
              {{ $t("email_templates.empty_preview") }}
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import MainMenu from "./../widgets/MainMenu";
import settings from "./../backend/LocalSettings";

export default {
  name: "email-templates",
  components: {
    "mainmenu": MainMenu
  },
  data() {
    return {
      loading: false,
      saving: false,
      PreviewTimer: null,
      EditorFullscreen: false,
      LastSavedHash: "",
      Templates: [],
      Languages: [],
      Fields: [],
      TemplateBodies: {},
      Payload: {},
      Form: {
        Event: "",
        Language: "de",
        Subject: "",
        BodyText: ""
      },
      Preview: {
        Subject: "",
        BodyHtml: ""
      }
    };
  },
  computed: {
    CurrentTemplate: function() {
      var selected = this.Form.Event;
      return this.Templates.find(function(template) {
        return template.Id === selected;
      });
    },
    HasUnsavedChanges: function() {
      return this.LastSavedHash !== "" && this.current_template_hash() !== this.LastSavedHash;
    },
    selected_template_label: function() {
      return this.CurrentTemplate ? this.CurrentTemplate.Name : "-";
    }
  },
  methods: {
    load_templates: function() {
      this.loading = true;
      this.$http.post("notification_preview/templates", {}).then(function(response) {
        this.loading = false;
        if(!settings.verify_response(response, this.$rights)) {
          this.$router.push("/");
          return;
        }
        if(response.body.Error) {
          this.$message.error(response.body.ErrorMessage || this.$t("email_templates.load_failed"));
          return;
        }

        var definitions = response.body.Definitions || {};
        this.Templates = definitions.Templates || [];
        this.Languages = definitions.Languages || [];
        this.Fields = definitions.Fields || [];
        this.TemplateBodies = definitions.TemplateBodies || {};
        this.Payload = Object.assign({}, definitions.Defaults || {});

        if(!this.Form.Event && this.Templates.length > 0) this.Form.Event = this.Templates[0].Id;
        if(this.Languages.length > 0 && !this.Languages.some((language) => language.Id === this.Form.Language)) {
          this.Form.Language = this.Languages[0].Id;
        }
        this.reset_template_text();
      }, function() {
        this.loading = false;
        this.$message.error(this.$t("email_templates.load_failed"));
      });
    },
    on_template_change: function() {
      this.reset_template_text();
    },
    on_language_change: function() {
      this.reset_template_text();
    },
    current_template_text: function() {
      if(!this.TemplateBodies[this.Form.Event]) return null;
      return this.TemplateBodies[this.Form.Event][this.Form.Language] || this.TemplateBodies[this.Form.Event].en || null;
    },
    reset_template_text: function() {
      var template = this.current_template_text();
      this.Form.Subject = template ? template.Subject : "";
      this.Form.BodyText = template ? template.BodyText : "";
      this.mark_saved_state();
      this.render_preview();
    },
    current_template_hash: function() {
      return JSON.stringify({
        Event: this.Form.Event,
        Language: this.Form.Language,
        Subject: this.Form.Subject || "",
        BodyText: this.Form.BodyText || ""
      });
    },
    mark_saved_state: function() {
      this.LastSavedHash = this.current_template_hash();
    },
    save_template: function() {
      if(!this.Form.Event || !this.Form.Language) return;

      this.saving = true;
      this.$http.post("notification_preview/save", {
        Event: this.Form.Event,
        Language: this.Form.Language,
        Subject: this.Form.Subject,
        BodyText: this.Form.BodyText
      }).then(function(response) {
        this.saving = false;
        if(!settings.verify_response(response, this.$rights)) {
          this.$router.push("/");
          return;
        }
        if(response.body.Error) {
          this.$message.error(response.body.ErrorMessage || this.$t("email_templates.save_failed"));
          return;
        }

        if(response.body.TemplateBodies) {
          this.TemplateBodies = response.body.TemplateBodies;
        }
        else {
          if(!this.TemplateBodies[this.Form.Event]) this.$set(this.TemplateBodies, this.Form.Event, {});
          this.$set(this.TemplateBodies[this.Form.Event], this.Form.Language, {
            Subject: this.Form.Subject,
            BodyText: this.Form.BodyText
          });
        }

        this.mark_saved_state();
        this.render_preview();
        this.$message.success(this.$t("email_templates.save_success"));
      }, function() {
        this.saving = false;
        this.$message.error(this.$t("email_templates.save_failed"));
      });
    },
    schedule_preview: function() {
      clearTimeout(this.PreviewTimer);
      this.PreviewTimer = setTimeout(this.render_preview, 180);
    },
    render_preview: function() {
      if(!this.Form.Event || !this.Form.Language) return;
      this.$http.post("notification_preview/render", {
        Event: this.Form.Event,
        Language: this.Form.Language,
        Subject: this.Form.Subject,
        BodyText: this.Form.BodyText,
        Payload: JSON.stringify(this.Payload)
      }).then(function(response) {
        if(!settings.verify_response(response, this.$rights)) {
          this.$router.push("/");
          return;
        }
        if(response.body.Error) {
          this.$message.error(response.body.ErrorMessage || this.$t("email_templates.preview_failed"));
          return;
        }
        this.Preview = response.body.Preview || { Subject: "", BodyHtml: "" };
      }, function() {
        this.$message.error(this.$t("email_templates.preview_failed"));
      });
    },
    primary_placeholder: function(label) {
      var parts = (label || "").split("/").map(function(part) {
        return part.trim();
      });
      if(this.Form.Language === "en" && parts.length > 1) return parts[1];
      return parts[0];
    },
    append_placeholder: function(field) {
      var placeholder = this.primary_placeholder(field.Placeholder);
      this.insert_body_text(placeholder);
    },
    format_body: function(style) {
      if(style === "bold") return this.wrap_body_selection("**", "**");
      if(style === "italic") return this.wrap_body_selection("*", "*", ["_"]);
      if(style === "underline") return this.wrap_body_selection("++", "++");
      if(style === "clear") return this.clear_body_formatting();
      if(style === "ul") return this.transform_body_lines(function(line) {
        if(line.trim() === "") return line;
        var indent = line.match(/^\s*/)[0];
        var content = line.slice(indent.length).replace(/^([-*]\s+|\d+\.\s+)/, "");
        return indent + "- " + content;
      });
      if(style === "ol") return this.transform_body_lines(function(line, index) {
        if(line.trim() === "") return line;
        var indent = line.match(/^\s*/)[0];
        var content = line.slice(indent.length).replace(/^([-*]\s+|\d+\.\s+)/, "");
        return indent + (index + 1) + ". " + content;
      });
      if(style === "indent") return this.transform_body_lines(function(line) {
        return line.trim() === "" ? line : "  " + line;
      });
      if(style === "outdent") return this.transform_body_lines(function(line) {
        return line.replace(/^ {1,2}/, "");
      });
      if(style === "link") return this.insert_link();
      if(style === "unlink") return this.clear_links();
      if(style === "table") return this.insert_body_text("\n| Field | Value |\n| --- | --- |\n| Item | [Artikelname] |\n| Quantity | [Menge] |\n");
      if(style === "hr") return this.insert_body_text("\n---\n");
      if(style === "paragraph") return this.transform_body_lines(function(line) {
        return line.replace(/^\s{0,3}#{1,3}\s+/, "").replace(/^\s{0,3}>\s?/, "");
      });
      if(style === "heading") return this.transform_body_lines(function(line) {
        if(line.trim() === "") return line;
        return "## " + line.replace(/^\s{0,3}#{1,3}\s+/, "");
      });
      if(style === "subheading") return this.transform_body_lines(function(line) {
        if(line.trim() === "") return line;
        return "### " + line.replace(/^\s{0,3}#{1,3}\s+/, "");
      });
      if(style === "note") return this.transform_body_lines(function(line) {
        if(line.trim() === "") return line;
        return "[small]" + line.replace(/^\[small\]/, "").replace(/\[\/small\]$/, "") + "[/small]";
      });
      if(style === "quote") return this.transform_body_lines(function(line) {
        return line.trim() === "" ? line : "> " + line.replace(/^\s{0,3}>\s?/, "");
      });
    },
    body_textarea: function() {
      return this.$refs.body_editor && this.$refs.body_editor.$refs
        ? this.$refs.body_editor.$refs.textarea
        : null;
    },
    selected_range: function() {
      var textarea = this.body_textarea();
      var value = this.Form.BodyText || "";
      return {
        textarea: textarea,
        start: textarea ? textarea.selectionStart : value.length,
        end: textarea ? textarea.selectionEnd : value.length
      };
    },
    set_body_text: function(value, start, end) {
      this.Form.BodyText = value;
      this.$nextTick(function() {
        var textarea = this.body_textarea();
        if(textarea) {
          textarea.focus();
          textarea.setSelectionRange(start, end);
        }
      });
      this.schedule_preview();
    },
    insert_body_text: function(text) {
      var range = this.selected_range();
      var value = this.Form.BodyText || "";
      var next = value.slice(0, range.start) + text + value.slice(range.end);
      var cursor = range.start + text.length;
      this.set_body_text(next, cursor, cursor);
    },
    wrap_body_selection: function(prefix, suffix, alternatePrefixes) {
      var range = this.selected_range();
      var value = this.Form.BodyText || "";
      var selected = value.slice(range.start, range.end);
      var suffixText = suffix == null ? prefix : suffix;
      var markerPairs = [{ prefix: prefix, suffix: suffixText }];
      (alternatePrefixes || []).forEach(function(marker) {
        markerPairs.push({ prefix: marker, suffix: marker });
      });

      for(var i = 0; i < markerPairs.length; i++) {
        var pair = markerPairs[i];
        var before = value.slice(range.start - pair.prefix.length, range.start);
        var after = value.slice(range.end, range.end + pair.suffix.length);

        if(pair.prefix.length === 1 && pair.suffix.length === 1 && pair.prefix === pair.suffix) {
          var wrappedMatch = selected.match(new RegExp("^\\" + pair.prefix + "+([\\s\\S]*?)\\" + pair.suffix + "+$"));
          if(wrappedMatch && wrappedMatch[1] !== "") {
            var cleanWrapped = wrappedMatch[1];
            var nextWrapped = value.slice(0, range.start) + cleanWrapped + value.slice(range.end);
            this.set_body_text(nextWrapped, range.start, range.start + cleanWrapped.length);
            return;
          }
        }

        if(selected.indexOf(pair.prefix) === 0 && selected.slice(-pair.suffix.length) === pair.suffix && selected.length >= pair.prefix.length + pair.suffix.length) {
          var unwrapped = selected.slice(pair.prefix.length, selected.length - pair.suffix.length);
          var nextSelected = value.slice(0, range.start) + unwrapped + value.slice(range.end);
          this.set_body_text(nextSelected, range.start, range.start + unwrapped.length);
          return;
        }

        if(before === pair.prefix && after === pair.suffix) {
          var removeBefore = pair.prefix.length;
          var removeAfter = pair.suffix.length;

          if(pair.prefix.length === 1 && pair.suffix.length === 1 && pair.prefix === pair.suffix) {
            while(range.start - removeBefore - 1 >= 0 && value.charAt(range.start - removeBefore - 1) === pair.prefix) {
              removeBefore++;
            }
            while(range.end + removeAfter < value.length && value.charAt(range.end + removeAfter) === pair.suffix) {
              removeAfter++;
            }
          }

          var nextAdjacent = value.slice(0, range.start - removeBefore) + selected + value.slice(range.end + removeAfter);
          var nextStart = range.start - removeBefore;
          this.set_body_text(nextAdjacent, nextStart, nextStart + selected.length);
          return;
        }
      }

      var next = value.slice(0, range.start) + prefix + selected + suffix + value.slice(range.end);
      var start = range.start + prefix.length;
      var end = start + selected.length;
      this.set_body_text(next, start, selected ? end : start);
    },
    selected_line_range: function() {
      var range = this.selected_range();
      var value = this.Form.BodyText || "";
      var start = value.lastIndexOf("\n", Math.max(0, range.start - 1)) + 1;
      var end = range.end;
      var nextLine = value.indexOf("\n", end);
      if(nextLine !== -1 && range.end > range.start) end = nextLine;
      return { start: start, end: end };
    },
    transform_body_lines: function(transformer) {
      var value = this.Form.BodyText || "";
      var lineRange = this.selected_line_range();
      var selected = value.slice(lineRange.start, lineRange.end);
      var lines = selected.split("\n");
      var nextSelected = lines.map(function(line, index) {
        return transformer(line, index);
      }).join("\n");
      var next = value.slice(0, lineRange.start) + nextSelected + value.slice(lineRange.end);
      this.set_body_text(next, lineRange.start, lineRange.start + nextSelected.length);
    },
    strip_formatting: function(text) {
      return (text || "")
        .replace(/\*\*([\s\S]*?)\*\*/g, "$1")
        .replace(/(^|[\s(])\*([^*\n]+?)\*(?=$|[\s).,;:!?])/gm, "$1$2")
        .replace(/\+\+([\s\S]*?)\+\+/g, "$1")
        .replace(/_([^_\n]+?)_/g, "$1")
        .replace(/\[small\]([\s\S]*?)\[\/small\]/g, "$1")
        .replace(/\[center\]([\s\S]*?)\[\/center\]/g, "$1")
        .replace(/\[right\]([\s\S]*?)\[\/right\]/g, "$1")
        .replace(/^\s{0,3}#{1,3}\s+/gm, "")
        .replace(/^\s{0,3}>\s?/gm, "");
    },
    clear_body_formatting: function() {
      var range = this.selected_range();
      var value = this.Form.BodyText || "";
      var selected = value.slice(range.start, range.end);
      if(selected === "") {
        this.set_body_text(this.strip_formatting(value), 0, 0);
        return;
      }
      var clean = this.strip_formatting(selected);
      var next = value.slice(0, range.start) + clean + value.slice(range.end);
      this.set_body_text(next, range.start, range.start + clean.length);
    },
    insert_link: function() {
      var range = this.selected_range();
      var value = this.Form.BodyText || "";
      var selected = value.slice(range.start, range.end) || "Link";
      this.$prompt(this.$t("email_templates.link_url_placeholder"), this.$t("email_templates.link"), {
        confirmButtonText: this.$t("confirm"),
        cancelButtonText: this.$t("cancel"),
        inputValue: "https://"
      }).then(function(result) {
        var url = (result.value || "").trim();
        if(url === "") return;
        var replacement = "[" + selected + "](" + url + ")";
        var next = value.slice(0, range.start) + replacement + value.slice(range.end);
        this.set_body_text(next, range.start, range.start + replacement.length);
      }.bind(this)).catch(function() {});
    },
    clear_links: function() {
      var range = this.selected_range();
      var value = this.Form.BodyText || "";
      var selected = value.slice(range.start, range.end);
      var target = selected || value;
      var clean = target.replace(/\[([^\]\n]+)\]\([^)]+\)/g, "$1");
      if(selected === "") {
        this.set_body_text(clean, 0, 0);
      }
      else {
        var next = value.slice(0, range.start) + clean + value.slice(range.end);
        this.set_body_text(next, range.start, range.start + clean.length);
      }
    },
    toggle_fullscreen: function() {
      this.EditorFullscreen = !this.EditorFullscreen;
      this.$nextTick(function() {
        var textarea = this.body_textarea();
        if(textarea) textarea.focus();
      });
    },
    before_unload: function(event) {
      if(!this.HasUnsavedChanges) return;
      event.preventDefault();
      event.returnValue = "";
    }
  },
  mounted: function() {
    settings.verify_login_and_redirect(this);
    window.addEventListener("beforeunload", this.before_unload);
    this.load_templates();
  },
  beforeRouteLeave: function(to, from, next) {
    if(!this.HasUnsavedChanges) {
      next();
      return;
    }

    this.$confirm(this.$t("email_templates.unsaved_leave_message"), this.$t("warning_title"), {
      confirmButtonText: this.$t("confirm"),
      cancelButtonText: this.$t("cancel"),
      type: "warning"
    }).then(function() {
      next();
    }).catch(function() {
      next(false);
    });
  },
  beforeDestroy: function() {
    clearTimeout(this.PreviewTimer);
    window.removeEventListener("beforeunload", this.before_unload);
  }
};
</script>

<style lang="less" scoped>
@import "../styles/admin-list.less";

.email-template-shell {
  min-height: calc(100vh - 56px);
  gap: 10px;
  padding-top: 14px;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;

  /deep/ .el-tag {
    height: 32px;
    line-height: 30px;
    border-color: #f2c37a;
    color: #a65f14;
    background: #fff8ec;
    font-weight: 700;
  }
}

.template-workbench {
  display: grid;
  grid-template-columns: 340px minmax(500px, 1fr) minmax(460px, 0.92fr);
  gap: 12px;
  align-items: stretch;
  height: calc(100vh - 180px);
  min-height: 620px;
}

.template-panel {
  min-height: 0;
  height: 100%;
  overflow: hidden;
  padding: 16px;
  background: #ffffff;
  border: 1px solid rgba(31, 73, 95, 0.14);
  border-radius: 8px;
  box-shadow: 0 14px 34px rgba(19, 55, 73, 0.12);
}

.setup-panel {
  position: sticky;
  top: 68px;
  display: flex;
  flex-direction: column;
}

.editor-panel,
.preview-panel {
  min-height: 0;
}

.editor-panel {
  display: flex;
  flex-direction: column;

  &.fullscreen {
    position: fixed;
    z-index: 3000;
    top: 18px;
    right: 18px;
    bottom: 18px;
    left: 18px;
    height: auto;
    box-shadow: 0 24px 70px rgba(10, 39, 52, 0.32);
  }
}

.preview-panel {
  display: flex;
  flex-direction: column;
}

.panel-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: -16px -16px 14px;
  padding: 13px 16px;
  border-bottom: 1px solid #edf1f4;
  background: #fbfcfd;

  h2 {
    margin: 0;
    color: #203746;
    font-size: 16px;
    line-height: 1.2;
    font-weight: 800;
  }

  &.compact {
    margin-top: 18px;
  }

  /deep/ .el-button {
    height: 28px;
    padding: 0 10px;
  }
}

.setup-static {
  flex: 0 0 auto;
}

.section-label {
  flex: 0 0 auto;
  margin: 4px -16px 10px;
  padding: 10px 16px;
  color: #203746;
  background: #fbfcfd;
  border-top: 1px solid #edf1f4;
  border-bottom: 1px solid #edf1f4;
  font-size: 16px;
  font-weight: 800;
}

.field-stack {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 11px;

  label {
    color: #526674;
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
  }

  small {
    color: #82919b;
    font-size: 11px;
  }
}

.option-help {
  display: block;
  max-width: 420px;
  color: #7a8790;
  font-size: 11px;
  line-height: 1.2;
}

.template-description {
  margin: 8px 0 10px;
  padding: 10px;
  color: #526674;
  background: #f6fafb;
  border: 1px solid #d7e7ed;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.4;
}

.sample-grid {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c8d8df;
    border-radius: 999px;
  }
}

.field-stack /deep/ .el-input__inner,
.field-stack /deep/ .el-textarea__inner {
  border-color: #d9e4ea;
  color: #314a5a;
  font-weight: 500;
}

.field-stack /deep/ .el-input__inner {
  height: 36px;
}

.body-editor {
  flex: 1;
  min-height: 0;

  /deep/ .el-textarea {
    flex: 1;
    min-height: 0;
    display: flex;
  }
}

.editor-toolbar {
  min-height: 39px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0;
  margin-bottom: -1px;
  padding: 4px 6px;
  background: #f4f8fb;
  border: 1px solid #d9e4ea;
  border-radius: 6px 6px 0 0;
}

.toolbar-group {
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 0 7px;
  border-right: 1px solid #d0dce3;

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    border-right: 0;
    padding-right: 0;
  }
}

.format-button {
  min-width: 27px;
  width: 27px;
  height: 27px;
  color: #284758;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 3px;
  font-size: 13px;
  font-weight: 800;
  line-height: 1;
  cursor: pointer;

  &:hover {
    color: #147d96;
    border-color: #8fc9d7;
    background: #ffffff;
  }

  i {
    font-size: 14px;
  }
}

.wide-button {
  width: 32px;
}

.muted-button {
  color: #a7b1b8;
}

.italic-button {
  font-style: italic;
  font-family: Georgia, "Times New Roman", serif;
}

.underline-button {
  text-decoration: underline;
}

.format-select {
  height: 27px;
  min-width: 95px;
  padding: 0 8px;
  color: #284758;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;

  &:hover {
    color: #147d96;
    border-color: #8fc9d7;
    background: #ffffff;
  }
}

.body-editor /deep/ textarea {
  height: 100%;
  min-height: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  line-height: 1.45;
  color: #304957;
  background: #fbfcfd;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.placeholder-bank {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid #edf1f4;

  button {
    height: 26px;
    padding: 0 9px;
    color: #27485a;
    background: #f1fafc;
    border: 1px solid #bfdde7;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
  }
}

.email-canvas {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding: 16px;
  background:
    linear-gradient(#f4f8fa, #f4f8fa) padding-box;
  border: 1px solid #d9e6ec;
  border-radius: 8px;
}

.preview-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
  color: #647783;
  font-size: 12px;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 700;
  }

  strong {
    flex: 0 0 auto;
    padding: 2px 7px;
    color: #203746;
    background: #ffffff;
    border: 1px solid #d7e4ea;
    border-radius: 999px;
    font-size: 11px;
  }
}

.email-preview {
  overflow: hidden;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  max-width: 760px;
  width: 100%;
  margin: 0 auto;
  background: #ffffff;
  border: 1px solid #d9e6ec;
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(39, 76, 92, 0.12);
}

.message-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px 10px;
  background: #ffffff;

  strong,
  span {
    display: block;
  }

  strong {
    color: #1d3545;
    font-size: 14px;
    font-weight: 800;
  }

  span {
    margin-top: 2px;
    color: #718391;
    font-size: 12px;
  }
}

.message-avatar {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background: #1b91a7;
  border-radius: 50%;
  font-size: 13px;
  font-weight: 800;
}

.email-header {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 12px 16px 14px;
  background: #fbfcfd;
  border-bottom: 1px solid #d9e6ec;

  span {
    color: #738391;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
  }

  strong {
    color: #152d3b;
    font-size: 17px;
    line-height: 1.3;
  }
}

.email-body {
  flex: 0 0 auto;
  overflow: visible;
  padding: 18px 20px 22px;
  color: #203746;
  background: #ffffff;
  font-size: 14px;
  line-height: 1.55;
}

.empty-preview {
  flex: 1;
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 26px;
  color: #738391;
  text-align: center;
  background: #f5f8fa;
  border: 1px dashed #c7d9e1;
  border-radius: 8px;
}

@media (max-width: 1280px) {
  .template-workbench {
    grid-template-columns: 1fr;
    height: auto;
    min-height: 0;
  }

  .setup-panel {
    position: static;
  }

  .editor-panel,
  .preview-panel {
    min-height: 0;
  }
}

@media (max-width: 720px) {
  .sample-grid {
    grid-template-columns: 1fr;
  }

  .admin-toolbar,
  .panel-heading {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
